/**
 * useProgressTotals — acumulados + racha desde el backend (`/progress/totals`,
 * agregación por día tz-aware). Es la FUENTE CANÓNICA de la racha en la app:
 * reemplaza a los cómputos client-side sobre `listSessions` (computeStreakDays
 * / currentStreak de streakData), que truncaban en 200 sesiones y podían
 * mostrar números distintos entre superficies.
 *
 * `totals === null` = sin dato (cargando o error de red): las superficies
 * esconden la métrica en vez de inventar un 0 — un "racha 0" falso es
 * indistinguible de una racha cortada de verdad.
 *
 * Cache de sesión (stale-while-revalidate, ver lib/sessionCache.ts) + dedup
 * del fetch en vuelo: varias superficies montan este hook a la vez (Home +
 * DesktopSidebar, Perfil + DesktopSidebar) y sin esto cada una pegaba su
 * propio GET y podía divergir de su vecina ante red flaky.
 */
import { useEffect, useState } from 'react';
import {
  browserTimeZone,
  getProgressTotals,
  type ProgressTotals,
} from '../lib/englishApi.js';
import { createSessionCache } from '../lib/sessionCache.js';
import { onProgressChanged } from '../lib/progressRefresh.js';

export interface ProgressTotalsState {
  totals: ProgressTotals | null;
  loading: boolean;
}

const totalsCache = createSessionCache<ProgressTotals>();

let inflight: Promise<ProgressTotals> | null = null;

/** Un solo GET compartido entre los hooks montados en simultáneo. */
function fetchTotalsShared(): Promise<ProgressTotals> {
  inflight ??= getProgressTotals(browserTimeZone()).finally(() => {
    inflight = null;
  });
  return inflight;
}

export function useProgressTotals(): ProgressTotalsState {
  const [totals, setTotals] = useState<ProgressTotals | null>(() => totalsCache.get());
  const [loading, setLoading] = useState(totalsCache.get() === null);

  useEffect(() => {
    let alive = true;
    const load = (): void => {
      void fetchTotalsShared()
        .then((t) => {
          totalsCache.set(t);
          if (alive) setTotals(t);
        })
        .catch(() => {
          // Sin dato: el consumidor oculta la métrica (no se muestra un 0 falso).
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    };
    load();
    // Un repaso hecho encima de la pantalla mueve la racha (card #604).
    const off = onProgressChanged(load);
    return () => {
      alive = false;
      off();
    };
  }, []);

  return { totals, loading };
}
